import { DEFAULT_GB_INDEX_FIELDS } from '@/constants/benchmarks/IndexConstants.ts';
import { LeaderboardOptionTypeEnum } from '@/enums/powerMarkets/LeaderboardEnums.ts';
import { IDateRangeCompositeNumbers } from '@/interfaces/general/IDateRange.ts';
import {
    IBenchmarkPoolApprovalContext,
    IBenchmarkPoolDefinition,
    ILeaderboardBenchmarkPoolCategory,
    ILeaderboardBenchmarkPoolConfig,
    ILeaderboardBenchmarkPoolReference,
    LeaderboardPoolingModeEnum,
} from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { ILeaderboardGroup } from '@/interfaces/powerMarkets/leaderboard/ILeaderboardGroup.ts';
import { benchmarkPoolRecordToInput } from '@/helpers/powerMarkets/benchmarkPoolDefinitionHelpers.ts';
import { dateFormatToAnyFormat, getDateObjectFromDatePicker } from '@/utils/dateUtils.ts';
import { cloneDeep } from '@/utils/global.ts';
import { useBenchmarkPoolSessionStore, type IBenchmarkPoolResolution } from '@/stores/powerMarkets/benchmarkPoolSessionStore.ts';

import { BenchmarkPoolApprovalRequiredError } from '@/helpers/powerMarkets/benchmarkPoolErrors.ts';

export { indexToBenchmarkPoolAssetFilter } from '@/helpers/powerMarkets/benchmarkPoolDefinitionHelpers.ts';

export function createDefaultBenchmarkPoolCategory (): ILeaderboardBenchmarkPoolConfig['categories'][number] {
    return {
        id: crypto.randomUUID(),
        indexFilters: cloneDeep(DEFAULT_GB_INDEX_FIELDS),
    };
}

export function createDefaultBenchmarkPoolConfig (): ILeaderboardBenchmarkPoolConfig {
    return {
        benchmarkPlantId: '',
        requiredPoolSize: 10,
        categories: [createDefaultBenchmarkPoolCategory()],
    };
}

export function isAssetPoolLeaderboard (group: ILeaderboardGroup | null | undefined): boolean {
    return group?.poolingMode === LeaderboardPoolingModeEnum.AssetPool;
}

export function getBenchmarkPoolCategoryDisplayLabel (category: ILeaderboardBenchmarkPoolCategory): string {
    const trimmedName = category.name?.trim();
    if (trimmedName) return trimmedName;

    const plants = category.indexFilters.selectedPlants ?? [];
    if (plants.length === 1) return plants[0];
    if (plants.length > 1) return `${plants.length} plants`;

    const fuels = category.indexFilters.selectedFuel?.map(f => f.name).filter(Boolean) ?? [];
    if (fuels.length) return fuels.join(', ');

    return 'Parameter filter';
}

export function buildPoolDefinitionFromRecord (record: {
    id: string;
    name: string;
    config: ILeaderboardBenchmarkPoolConfig;
}): IBenchmarkPoolDefinition | null {
    if (!record.config.benchmarkPlantId || !record.config.categories.length) return null;
    return { id: record.id, ...benchmarkPoolRecordToInput(record) };
}

export async function buildPoolDefinitionFromGroup (group: ILeaderboardGroup): Promise<IBenchmarkPoolDefinition | null> {
    if (!isAssetPoolLeaderboard(group) || !group.benchmarkPoolId) return null;
    const { benchmarkPoolsApi } = await import('@/api/powerMarkets/benchmarkPoolsApi.ts');
    const record = await benchmarkPoolsApi.getBenchmarkPool(group.benchmarkPoolId);
    return buildPoolDefinitionFromRecord(record);
}

export async function getBenchmarkPoolConfigFingerprint (group: ILeaderboardGroup): Promise<string | null> {
    const definition = await buildPoolDefinitionFromGroup(group);
    if (!definition) return null;
    return JSON.stringify(definition);
}

export interface IResolveBenchmarkPoolOptions {
    date?: IDateRangeCompositeNumbers;
    approveUndersizedPool?: boolean;
    signal?: AbortSignal;
    approvalContext?: IBenchmarkPoolApprovalContext;
    forceResolve?: boolean;
    onResolved?: (result: IResolveBenchmarkPoolResult) => void;
}

export type IResolveBenchmarkPoolResult = IBenchmarkPoolResolution;

function getGroupDate (group: ILeaderboardGroup): IDateRangeCompositeNumbers {
    return dateFormatToAnyFormat<IDateRangeCompositeNumbers>(
        getDateObjectFromDatePicker(group.userOptions.selectedDateModel),
        'IDateRangeCompositeNumbers',
    );
}

function calendarDate (year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export async function resolveBenchmarkPoolForGroup (
    group: ILeaderboardGroup,
    options: IResolveBenchmarkPoolOptions = {},
): Promise<IResolveBenchmarkPoolResult> {
    const poolId = group.benchmarkPoolId;
    const date = { ...(options.date ?? getGroupDate(group)) };
    function assertCurrent (): void {
        if (!isAssetPoolLeaderboard(group) || group.benchmarkPoolId !== poolId
            || (!options.date && JSON.stringify(getGroupDate(group)) !== JSON.stringify(date))) {
            throw new DOMException('Benchmark pool request was superseded.', 'AbortError');
        }
    }
    if (!poolId) throw new Error('Asset pool configuration is incomplete.');
    return resolveBenchmarkPoolForPeriod(poolId, date, options, assertCurrent);
}

export async function resolveBenchmarkPoolForPeriod (
    poolId: string,
    date: IDateRangeCompositeNumbers,
    options: IResolveBenchmarkPoolOptions = {},
    assertConsumerCurrent: () => void = () => {},
): Promise<IResolveBenchmarkPoolResult> {
    const store = useBenchmarkPoolSessionStore();
    const session = store.getSession();
    const from = calendarDate(date.fromYear, date.fromMonth, date.fromDay);
    const to = calendarDate(date.toYear, date.toMonth, date.toDay);
    function assertCurrent (): void {
        assertConsumerCurrent();
        if (options.signal?.aborted || store.getSession() !== session) {
            throw new DOMException('Benchmark pool request was superseded.', 'AbortError');
        }
    }
    assertCurrent();
    // Refresh at each data operation, including reuse, so another editor's save is visible.
    const { benchmarkPoolsApi } = await import('@/api/powerMarkets/benchmarkPoolsApi.ts');
    const definition = buildPoolDefinitionFromRecord(await benchmarkPoolsApi.getBenchmarkPool(poolId));
    assertCurrent();
    if (!definition) throw new Error('Asset pool configuration is incomplete.');
    const fingerprint = JSON.stringify(definition);
    const key = JSON.stringify([fingerprint, from, to]);
    const context = options.approvalContext;
    const approve = !!options.approveUndersizedPool && (!context || (
        context.sessionId === session.id && context.configFingerprint === fingerprint
        && context.from === from && context.to === to
    ));
    if (options.forceResolve) session.resolutions.delete(key);
    let pending = session.resolutions.get(key);
    if (!pending) {
        pending = (async (): Promise<IResolveBenchmarkPoolResult> => {
            const resolved = await benchmarkPoolsApi.postBenchmarkPoolPreview({
                poolId: definition.id,
                from,
                to,
                approveUndersizedPool: approve,
            });
            if (store.getSession() !== session) {
                throw new DOMException('Benchmark pool session changed.', 'AbortError');
            }
            if (resolved.poolId !== poolId || !resolved.revisionId
                || resolved.from.slice(0, 10) !== from || resolved.to.slice(0, 10) !== to) {
                throw new Error('Benchmark pool preview did not match the requested pool and period.');
            }
            // Preview reads the saved definition itself. Never label a result with newer content.
            const current = await benchmarkPoolsApi.getBenchmarkPool(definition.id);
            if (JSON.stringify(buildPoolDefinitionFromRecord(current)) !== fingerprint) {
                throw new Error('The pool definition changed during preview. Retry to resolve the saved configuration.');
            }
            return {
                reference: { poolId: resolved.poolId, revisionId: resolved.revisionId },
                requiresUndersizedApproval: resolved.requiresUndersizedApproval,
                preview: resolved,
                approvalContext: {
                    sessionId: session.id, configFingerprint: fingerprint, from, to,
                },
            };
        })();
        session.resolutions.set(key, pending);
        const request = pending;
        // Unapproved previews have no persisted revision; failures must remain retryable.
        pending.then((result) => {
            if (result.requiresUndersizedApproval && session.resolutions.get(key) === request) {
                session.resolutions.delete(key);
            }
        }, () => {
            if (session.resolutions.get(key) === request) session.resolutions.delete(key);
        });
    }
    const result = await pending;
    assertCurrent();
    return cloneDeep(result);
}

export async function ensureLeaderboardBenchmarkPoolReference (
    group: ILeaderboardGroup,
    options: IResolveBenchmarkPoolOptions = {},
): Promise<ILeaderboardBenchmarkPoolReference | null> {
    if (!isAssetPoolLeaderboard(group)) return null;
    const result = await resolveBenchmarkPoolForGroup(group, options);
    if (result.requiresUndersizedApproval) {
        throw new BenchmarkPoolApprovalRequiredError(result.preview, result.approvalContext);
    }
    options.onResolved?.(result);
    return result.reference;
}

export function applyAssetPoolLeaderboardDefaults (group: ILeaderboardGroup): void {
    group.poolingMode = LeaderboardPoolingModeEnum.AssetPool;
    group.userOptions.leaderboardType = LeaderboardOptionTypeEnum.Plant;
}

export function applyNonPooledLeaderboardDefaults (group: ILeaderboardGroup): void {
    group.poolingMode = LeaderboardPoolingModeEnum.NonPooled;
    delete group.benchmarkPoolId;
    delete group.benchmarkPoolScope;
    delete group.benchmarkPoolReference;
}

export function sanitizeLeaderboardGroupForPersist (group: ILeaderboardGroup): ILeaderboardGroup {
    const snapshot = cloneDeep(group);
    delete snapshot.benchmarkPoolReference;
    return snapshot;
}
